import React, { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import { useSnackbar } from 'notistack';
import AddCustomField from '../modal/AddCustomField';

const SECTIONS = [
  { key: 'hr_user',               label: 'HR / Users'       },
  { key: 'crm_contact',          label: 'CRM Contact'      },
  { key: 'crm_organisation',     label: 'CRM Organisation' },
  { key: 'expenses',             label: 'Expenses'         },
  { key: 'finance_income',       label: 'Income'           },
  { key: 'finance_car',          label: 'Vehicles'         },
  { key: 'finance_car_expenses', label: 'Car Expenses'     },
  { key: 'insurance',            label: 'Insurance'        },
  { key: 'hr_driver_license',    label: 'Driver Licenses'  },
  { key: 'equipment',            label: 'Equipment'        },
] as const;

type SectionKey = typeof SECTIONS[number]['key'];
const SECTION_LABEL: Record<string, string> = Object.fromEntries(SECTIONS.map((s) => [s.key, s.label]));

const HR_SYSTEM_FIELDS: { fieldKey: string; label: string; isCore?: boolean }[] = [
  { fieldKey: 'first_name',                     label: 'First Name',     isCore: true  },
  { fieldKey: 'last_name',                      label: 'Last Name',      isCore: true  },
  { fieldKey: 'second_name',                    label: 'Middle Name'                   },
  { fieldKey: 'date_of_birth',                  label: 'Date of Birth'                 },
  { fieldKey: 'sex',                            label: 'Gender'                        },
  { fieldKey: 'email',                          label: 'Email',          isCore: true  },
  { fieldKey: 'mobile_phone',                   label: 'Mobile Phone'                  },
  { fieldKey: 'city',                           label: 'City'                          },
  { fieldKey: 'address',                        label: 'Address'                       },
  { fieldKey: 'postcode',                       label: 'Postcode'                      },
  { fieldKey: 'job_title',                      label: 'Job Title'                     },
  { fieldKey: 'departmentId',                   label: 'Department'                    },
  { fieldKey: 'supervisorId',                   label: 'Supervisor',     isCore: true  },
  { fieldKey: 'workingStatusId',                label: 'Working Status'                },
  { fieldKey: 'contract_start_date',            label: 'Contract Start'                },
  { fieldKey: 'probation_start_date',           label: 'Probation Start'               },
  { fieldKey: 'probation_end_date',             label: 'Probation End'                 },
  { fieldKey: 'notice_period',                  label: 'Notice Period'                 },
  { fieldKey: 'emergency_contact_name',         label: 'Emergency Name'                },
  { fieldKey: 'emergency_contact_relationship', label: 'Relationship'                  },
  { fieldKey: 'emergency_contact_phone',        label: 'Emergency Phone'               },
  { fieldKey: 'emergency_contact_email',        label: 'Emergency Email'               },
  { fieldKey: 'avatarUrl',                      label: 'Profile Photo'                 },
  { fieldKey: 'time_zone',                      label: 'Timezone'                      },
];

const CRM_SYSTEM_FIELDS: { fieldKey: string; label: string; isCore?: boolean }[] = [
  { fieldKey: 'firstName',      label: 'First Name',    isCore: true  },
  { fieldKey: 'lastName',       label: 'Last Name'                    },
  { fieldKey: 'middleName',     label: 'Middle Name'                  },
  { fieldKey: 'contactType',    label: 'Contact Type',  isCore: true  },
  { fieldKey: 'organisationId', label: 'Organisation'                 },
  { fieldKey: 'email',          label: 'Email'                        },
  { fieldKey: 'phone1',         label: 'Mobile Phone'                 },
  { fieldKey: 'phone2',         label: 'Work Phone'                   },
  { fieldKey: 'jobTitle',       label: 'Job Title'                    },
  { fieldKey: 'department',     label: 'Department'                   },
  { fieldKey: 'gender',         label: 'Gender'                       },
  { fieldKey: 'dateOfBirth',    label: 'Date of Birth'                },
  { fieldKey: 'notes',          label: 'Description'                  },
];

const EXPENSE_SYSTEM_FIELDS: { fieldKey: string; label: string }[] = [
  { fieldKey: 'name',                    label: 'Name / Payee'    },
  { fieldKey: 'contact_number',          label: 'Contact Number'  },
  { fieldKey: 'description',             label: 'Description'     },
  { fieldKey: 'project',                 label: 'Project'         },
  { fieldKey: 'account_number',          label: 'Account Number'  },
  { fieldKey: 'iban',                    label: 'IBAN'            },
  { fieldKey: 'sort_code',               label: 'Sort Code'       },
  { fieldKey: 'finance_additional_info', label: 'Additional Info' },
  { fieldKey: 'nominal_department',      label: 'Nominal Dept'    },
];

const CAR_EXPENSES_SYSTEM_FIELDS: { fieldKey: string; label: string }[] = [
  { fieldKey: 'destination',              label: 'Destination'    },
  { fieldKey: 'purpose',                  label: 'Purpose'        },
  { fieldKey: 'distance',                 label: 'Distance'       },
  { fieldKey: 'unit',                     label: 'Unit'           },
  { fieldKey: 'starting_point',           label: 'Starting Point' },
  { fieldKey: 'sort_code',                label: 'Sort Code'      },
  { fieldKey: 'account_number',           label: 'Account Number' },
  { fieldKey: 'iban',                     label: 'IBAN'           },
  { fieldKey: 'finance_additional_info',  label: 'Additional Info'},
  { fieldKey: 'additional_info',          label: 'Notes'          },
];

const INCOME_SYSTEM_FIELDS: { fieldKey: string; label: string }[] = [
  { fieldKey: 'name',           label: 'Name / Source'  },
  { fieldKey: 'description',    label: 'Description'    },
  { fieldKey: 'status_comment', label: 'Status Comment' },
];

const DRIVER_LICENSE_SYSTEM_FIELDS: { fieldKey: string; label: string }[] = [
  { fieldKey: 'license_number',  label: 'License Number' },
  { fieldKey: 'vehicle_class',   label: 'Vehicle Class'  },
  { fieldKey: 'issue_date',      label: 'Issue Date'     },
  { fieldKey: 'expiry_date',     label: 'Expiry Date'    },
  { fieldKey: 'photo_front_url', label: 'Photo (Front)'  },
  { fieldKey: 'photo_back_url',  label: 'Photo (Back)'   },
];

const GENERIC_SYSTEM_KEYS: Partial<Record<SectionKey, { fieldKey: string; label: string }[]>> = {
  finance_car_expenses: CAR_EXPENSES_SYSTEM_FIELDS,
  finance_income:       INCOME_SYSTEM_FIELDS,
  hr_driver_license:    DRIVER_LICENSE_SYSTEM_FIELDS,
};

interface UnifiedRow {
  id?: number;
  rowKey: string;
  sectionKey: SectionKey;
  fieldKey: string;
  label: string;
  description?: string | null;
  type?: string;
  isCore: boolean;
  isCustom: boolean;
  isVisible: boolean;
  isRequired: boolean;
}

function Toggle({ checked, disabled, onChange, red }: {
  checked: boolean; disabled?: boolean; onChange: () => void; red?: boolean;
}) {
  return (
    <label
      style={{ display: 'inline-flex', alignItems: 'center', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.38 : 1 }}
      onClick={(e) => { e.stopPropagation(); if (!disabled) onChange(); }}
    >
      <span style={{ position: 'relative', display: 'inline-block', width: 32, height: 18, flexShrink: 0 }}>
        <span style={{
          position: 'absolute', inset: 0, borderRadius: 99,
          background: checked ? (red ? '#dc2626' : '#16a34a') : '#d1d5db',
          transition: 'background .16s',
        }} />
        <span style={{
          position: 'absolute', top: 2, left: checked ? 16 : 2, width: 14, height: 14,
          borderRadius: '50%', background: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,.22)', transition: 'left .16s',
        }} />
      </span>
    </label>
  );
}

const thStyle: React.CSSProperties = {
  textAlign: 'left', fontSize: 9, fontWeight: 700,
  color: 'var(--text-color-calendar-week)', textTransform: 'uppercase',
  letterSpacing: '.05em', padding: '6px 10px',
  background: 'var(--bg-form)',
  boxShadow: 'inset 0 -1px 0 var(--border-new)',
  position: 'sticky', top: 0, zIndex: 2,
};
const thCenter: React.CSSProperties = { ...thStyle, textAlign: 'center', width: 64 };
const tdStyle: React.CSSProperties = {
  padding: '7px 10px', borderBottom: '1px solid var(--border-input)',
  fontSize: 12, verticalAlign: 'middle', color: 'var(--text-color)',
};
const tdCenter: React.CSSProperties = { ...tdStyle, textAlign: 'center' };

export default function FieldsSettings() {
  const { enqueueSnackbar } = useSnackbar();

  const [search, setSearch] = useState('');
  const [filterSection, setFilterSection] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editField, setEditField] = useState<any>(null);
  const [addSection, setAddSection] = useState<SectionKey>('hr_user');
  const searchRef = useRef<HTMLInputElement>(null);
  const filterRef = useRef<HTMLDivElement>(null);

  const [hrConfig, setHrConfig]   = useState<Record<string, { isVisible: boolean; isRequired: boolean }>>({});
  const [crmConfig, setCrmConfig] = useState<Record<string, { isVisible: boolean; isRequired: boolean }>>({});
  const [expFields, setExpFields] = useState<any[]>([]);
  const [allCustom, setAllCustom] = useState<any[]>([]);

  const load = useCallback(async () => {
    try {
      const [hrRes, crmRes, expRes, ...customRess] = await Promise.all([
        axios.get('hr/profile-field-config'),
        axios.get('crm/field-config?table=crm_contact'),
        axios.get('fields'),
        ...SECTIONS.map((s) => axios.get(`fields/custom?table=${s.key}`)),
      ]);

      const hrMap: Record<string, any> = {};
      (hrRes.data ?? []).forEach((c: any) => { hrMap[c.fieldKey] = c; });
      setHrConfig(hrMap);

      const crmMap: Record<string, any> = {};
      (crmRes.data ?? []).forEach((c: any) => { crmMap[c.fieldKey] = c; });
      setCrmConfig(crmMap);

      setExpFields(expRes.data ?? []);

      const custom: any[] = [];
      customRess.forEach((res, i) => {
        (res.data ?? []).forEach((f: any) => {
          custom.push({ ...f, _sectionKey: SECTIONS[i].key });
        });
      });
      setAllCustom(custom);
    } catch {
      enqueueSnackbar('Error loading fields', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  // ── Toggle handlers ──────────────────────────────────────────────────────────

  const toggleHr = async (fieldKey: string, prop: 'isVisible' | 'isRequired') => {
    const cur = hrConfig[fieldKey] ?? { isVisible: true, isRequired: false };
    const isVisible  = prop === 'isVisible'  ? !cur.isVisible  : cur.isVisible;
    const isRequired = prop === 'isRequired' ? !cur.isRequired : (prop === 'isVisible' && !isVisible ? false : cur.isRequired);
    setHrConfig((p) => ({ ...p, [fieldKey]: { isVisible, isRequired } }));
    try {
      await axios.post('hr/profile-field-config', { fieldKey, isVisible, isRequired, weight: 1 });
    } catch {
      setHrConfig((p) => ({ ...p, [fieldKey]: cur }));
      enqueueSnackbar('Error', { variant: 'error' });
    }
  };

  const toggleCrm = async (fieldKey: string, prop: 'isVisible' | 'isRequired') => {
    const cur = crmConfig[fieldKey] ?? { isVisible: true, isRequired: false };
    const isVisible  = prop === 'isVisible'  ? !cur.isVisible  : cur.isVisible;
    const isRequired = prop === 'isRequired' ? !cur.isRequired : (prop === 'isVisible' && !isVisible ? false : cur.isRequired);
    setCrmConfig((p) => ({ ...p, [fieldKey]: { isVisible, isRequired } }));
    try {
      await axios.post('crm/field-config', { tableName: 'crm_contact', fieldKey, isVisible, isRequired });
    } catch {
      setCrmConfig((p) => ({ ...p, [fieldKey]: cur }));
      enqueueSnackbar('Error', { variant: 'error' });
    }
  };

  const toggleExp = async (fieldKey: string, prop: 'isShown' | 'isRequired') => {
    const field = expFields.find((f) => f.column_name === fieldKey);
    const cur = { isShown: field?.isShown ?? true, isRequired: field?.isRequired ?? false };
    const updates: any = { [prop]: !cur[prop as keyof typeof cur] };
    if (prop === 'isShown' && cur.isShown) updates.isRequired = false;
    setExpFields((p) => p.map((f) => f.column_name === fieldKey ? { ...f, ...updates } : f));
    try {
      if (field?.id) {
        await axios.put(`fields/${field.id}`, updates);
      } else {
        const { data } = await axios.post('fields/add', { table_name: 'expenses', column_name: fieldKey, type: 'text', ...cur, ...updates });
        setExpFields((p) => p.map((f) => f.column_name === fieldKey ? { ...f, id: data.id } : f));
      }
    } catch {
      setExpFields((p) => p.map((f) => f.column_name === fieldKey ? { ...f, ...cur } : f));
      enqueueSnackbar('Error', { variant: 'error' });
    }
  };

  const toggleGeneric = async (sectionKey: string, fieldKey: string, prop: 'isShown' | 'isRequired') => {
    const existing = allCustom.find((f) => f._sectionKey === sectionKey && f.column_name === fieldKey);
    const cur = { isShown: existing?.isShown ?? true, isRequired: existing?.isRequired ?? false };
    const updates: any = { [prop]: !cur[prop as keyof typeof cur] };
    if (prop === 'isShown' && cur.isShown) updates.isRequired = false;
    if (existing) {
      setAllCustom((p) => p.map((f) => f._sectionKey === sectionKey && f.column_name === fieldKey ? { ...f, ...updates } : f));
    } else {
      setAllCustom((p) => [...p, { column_name: fieldKey, table_name: sectionKey, _sectionKey: sectionKey, ...cur, ...updates }]);
    }
    try {
      if (existing?.id) {
        await axios.put(`fields/${existing.id}`, updates);
      } else {
        const { data } = await axios.post('fields/add', { table_name: sectionKey, column_name: fieldKey, type: 'text', ...cur, ...updates });
        setAllCustom((p) => p.map((f) => f._sectionKey === sectionKey && f.column_name === fieldKey ? { ...f, id: data.id } : f));
      }
    } catch {
      if (existing) {
        setAllCustom((p) => p.map((f) => f._sectionKey === sectionKey && f.column_name === fieldKey ? { ...f, ...cur } : f));
      } else {
        setAllCustom((p) => p.filter((f) => !(f._sectionKey === sectionKey && f.column_name === fieldKey)));
      }
      enqueueSnackbar('Error', { variant: 'error' });
    }
  };

  const toggleCustom = async (id: number, prop: 'isShown' | 'isRequired') => {
    const field = allCustom.find((f) => f.id === id);
    const cur = { isShown: field?.isShown ?? true, isRequired: field?.isRequired ?? false };
    const updates: any = { [prop]: !cur[prop as keyof typeof cur] };
    if (prop === 'isShown' && cur.isShown) updates.isRequired = false;
    setAllCustom((p) => p.map((f) => f.id === id ? { ...f, ...updates } : f));
    try {
      await axios.put(`fields/${id}`, updates);
    } catch {
      setAllCustom((p) => p.map((f) => f.id === id ? { ...f, ...cur } : f));
      enqueueSnackbar('Error', { variant: 'error' });
    }
  };

  const handleToggleVisible = (row: UnifiedRow) => {
    if (row.isCustom)                      return toggleCustom(row.id!, 'isShown');
    if (row.sectionKey === 'hr_user')      return toggleHr(row.fieldKey, 'isVisible');
    if (row.sectionKey === 'crm_contact')  return toggleCrm(row.fieldKey, 'isVisible');
    if (row.sectionKey === 'expenses')     return toggleExp(row.fieldKey, 'isShown');
    return toggleGeneric(row.sectionKey, row.fieldKey, 'isShown');
  };

  const handleToggleRequired = (row: UnifiedRow) => {
    if (row.isCustom)                      return toggleCustom(row.id!, 'isRequired');
    if (row.sectionKey === 'hr_user')      return toggleHr(row.fieldKey, 'isRequired');
    if (row.sectionKey === 'crm_contact')  return toggleCrm(row.fieldKey, 'isRequired');
    if (row.sectionKey === 'expenses')     return toggleExp(row.fieldKey, 'isRequired');
    return toggleGeneric(row.sectionKey, row.fieldKey, 'isRequired');
  };

  const deleteCustomField = async (row: UnifiedRow) => {
    if (!window.confirm(`Delete "${row.label}"?`)) return;
    try {
      await axios.delete(`fields/${row.id}`);
      setAllCustom((p) => p.filter((f) => f.id !== row.id));
      enqueueSnackbar('Deleted', { variant: 'success' });
    } catch {
      enqueueSnackbar('Error deleting field', { variant: 'error' });
    }
  };

  // ── Build unified rows ───────────────────────────────────────────────────────

  const buildRows = (): UnifiedRow[] => {
    const rows: UnifiedRow[] = [];

    // HR system
    HR_SYSTEM_FIELDS.forEach((f) => {
      const cfg = hrConfig[f.fieldKey] ?? { isVisible: true, isRequired: false };
      rows.push({ rowKey: `hr_user__${f.fieldKey}`, sectionKey: 'hr_user', fieldKey: f.fieldKey, label: f.label, isCore: f.isCore ?? false, isCustom: false, ...cfg });
    });

    // CRM contact system
    CRM_SYSTEM_FIELDS.forEach((f) => {
      const cfg = crmConfig[f.fieldKey] ?? { isVisible: true, isRequired: false };
      rows.push({ rowKey: `crm_contact__${f.fieldKey}`, sectionKey: 'crm_contact', fieldKey: f.fieldKey, label: f.label, isCore: f.isCore ?? false, isCustom: false, ...cfg });
    });

    // Expense system
    EXPENSE_SYSTEM_FIELDS.forEach((f) => {
      const ef = expFields.find((e) => e.column_name === f.fieldKey);
      rows.push({
        rowKey: `expenses__${f.fieldKey}`, sectionKey: 'expenses', fieldKey: f.fieldKey, label: f.label,
        isCore: false, isCustom: false,
        isVisible: ef?.isShown ?? true, isRequired: ef?.isRequired ?? false,
      });
    });

    // Generic system sections (car expenses, income, driver license)
    (Object.entries(GENERIC_SYSTEM_KEYS) as [SectionKey, { fieldKey: string; label: string }[]][]).forEach(([secKey, fields]) => {
      fields.forEach((f) => {
        const cf = allCustom.find((c) => c._sectionKey === secKey && c.column_name === f.fieldKey);
        rows.push({
          rowKey: `${secKey}__sys__${f.fieldKey}`, sectionKey: secKey, fieldKey: f.fieldKey, label: f.label,
          isCore: false, isCustom: false,
          isVisible: cf?.isShown ?? true, isRequired: cf?.isRequired ?? false,
        });
      });
    });

    // All custom fields
    const genericSystemKeys = new Map<string, Set<string>>();
    Object.entries(GENERIC_SYSTEM_KEYS).forEach(([sec, fields]) => {
      genericSystemKeys.set(sec, new Set(fields.map((f) => f.fieldKey)));
    });

    allCustom.forEach((f) => {
      // Skip if already shown as a generic system field
      const systemSet = genericSystemKeys.get(f._sectionKey);
      if (systemSet?.has(f.column_name)) return;

      const labelRaw = f.column_name.replace(/_/g, ' ');
      const label = labelRaw.charAt(0).toUpperCase() + labelRaw.slice(1);
      rows.push({
        id: f.id,
        rowKey: `custom__${f.id}`,
        sectionKey: f._sectionKey,
        fieldKey: f.column_name,
        label,
        description: f.description,
        type: f.type,
        isCore: false,
        isCustom: true,
        isVisible: f.isShown ?? true,
        isRequired: f.isRequired ?? false,
      });
    });

    return rows;
  };

  const allRows = buildRows();
  const visibleRows = allRows.filter((row) => {
    if (filterSection && row.sectionKey !== filterSection) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        row.label.toLowerCase().includes(q) ||
        (row.description ?? '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  const openAdd = () => {
    setEditField(null);
    setAddSection((filterSection as SectionKey) || 'hr_user');
    setAddOpen(true);
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg-form)' }}>

      {/* Toolbar */}
      <div style={{
        padding: '10px 14px 8px',
        borderBottom: '1px solid var(--border-input)',
        display: 'flex', gap: 8, alignItems: 'center',
        background: 'var(--bg-form)',
      }}>
        {/* Search bar — click anywhere in empty area to focus input */}
        <div
          style={{
            flex: 1, display: 'flex', alignItems: 'center', gap: 7,
            height: 32, padding: '0 10px',
            border: '1px solid var(--border-input)', borderRadius: 7,
            background: 'var(--bg-input)', cursor: 'text',
          }}
          onClick={() => searchRef.current?.focus()}
        >
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, color: 'var(--text-color-calendar-week)' }}>
            <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.6"/>
            <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          </svg>
          <input
            ref={searchRef}
            type="text"
            placeholder={t('Search') || 'Search fields...'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            style={{
              flex: 1, border: 'none', background: 'transparent',
              color: 'var(--text-color)', fontSize: 12, outline: 'none',
            }}
          />
        </div>

        {/* Filter button */}
        <div ref={filterRef} style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => setFilterOpen((v) => !v)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              height: 32, padding: '0 13px', borderRadius: 7, cursor: 'pointer',
              border: filterSection ? '2px solid #2563eb' : '1px solid var(--border-input)',
              background: filterSection ? 'rgba(37,99,235,.08)' : 'var(--bg-input)',
              color: filterSection ? '#2563eb' : 'var(--text-color-calendar-week)',
              fontSize: 12, fontWeight: filterSection ? 600 : 400,
              transition: 'border-color .15s, background .15s, color .15s',
            }}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
              <path d="M2 4h12M4 8h8M6 12h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
            {filterSection ? SECTION_LABEL[filterSection] : (t('Filter') || 'Filter')}
            {filterSection && (
              <span
                onMouseDown={(e) => { e.stopPropagation(); }}
                onClick={(e) => { e.stopPropagation(); setFilterSection(''); setFilterOpen(false); }}
                style={{ fontSize: 14, lineHeight: 1, marginLeft: 2, opacity: 0.6, padding: '0 1px' }}
              >×</span>
            )}
          </button>

          {filterOpen && (
            <div
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                position: 'absolute', top: 'calc(100% + 5px)', right: 0,
                background: 'var(--bg-form)', border: '1px solid var(--border-input)',
                borderRadius: 8, padding: 5, zIndex: 200, minWidth: 180,
                boxShadow: '0 4px 16px rgba(0,0,0,.12)',
              }}
            >
              {[{ key: '', label: t('AllSections') || 'All sections' }, ...SECTIONS].map((s) => (
                <div
                  key={s.key}
                  onClick={() => { setFilterSection(s.key); setFilterOpen(false); }}
                  style={{
                    padding: '6px 10px', borderRadius: 5, cursor: 'pointer', fontSize: 12,
                    color: filterSection === s.key ? '#2563eb' : 'var(--text-color-calendar-week)',
                    fontWeight: filterSection === s.key ? 600 : 400,
                    background: filterSection === s.key ? 'rgba(37,99,235,.07)' : 'transparent',
                  }}
                  onMouseEnter={(e) => { if (filterSection !== s.key) (e.currentTarget as HTMLElement).style.background = 'rgba(0,0,0,.04)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = filterSection === s.key ? 'rgba(37,99,235,.07)' : 'transparent'; }}
                >{s.label}</div>
              ))}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={openAdd}
          style={{
            height: 32, padding: '0 12px', borderRadius: 7,
            border: 'none', background: '#2563eb', color: '#fff',
            fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >+ {t('Add') || 'Add'}</button>
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, width: 130 }}>Section</th>
              <th style={thStyle}>Field Name</th>
              <th style={thStyle}>Description</th>
              <th style={thCenter}>Required</th>
              <th style={thCenter}>Active</th>
              <th style={{ ...thCenter, width: 60 }} />
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => (
              <tr
                key={row.rowKey}
                style={{ background: 'var(--btn-user-form)' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(0,0,0,.015)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--btn-user-form)'; }}
              >
                {/* Section chip */}
                <td style={tdStyle}>
                  <span style={{
                    display: 'inline-block', padding: '2px 7px', borderRadius: 4,
                    background: 'rgba(0,0,0,.06)', fontSize: 10, fontWeight: 600,
                    color: 'var(--text-color-calendar-week)', whiteSpace: 'nowrap',
                  }}>{SECTION_LABEL[row.sectionKey]}</span>
                </td>

                {/* Field name */}
                <td style={tdStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    {row.label}
                    {row.isCore && (
                      <span style={{
                        fontSize: 8, fontWeight: 800, color: '#7c3aed',
                        background: 'rgba(124,58,237,.1)', padding: '1px 5px',
                        borderRadius: 4, textTransform: 'uppercase', letterSpacing: '.03em',
                      }}>core</span>
                    )}
                    {row.isCustom && (
                      <span style={{
                        fontSize: 8, fontWeight: 800, color: 'var(--btn-user-btn)',
                        background: 'rgba(37,99,235,.09)', padding: '1px 5px',
                        borderRadius: 4, textTransform: 'uppercase',
                      }}>custom</span>
                    )}
                  </div>
                </td>

                {/* Description */}
                <td style={{ ...tdStyle, color: 'var(--text-color-calendar-week)', fontSize: 11 }}>
                  {row.description || '—'}
                </td>

                {/* Required */}
                <td style={tdCenter}>
                  <Toggle
                    checked={row.isRequired}
                    disabled={row.isCore || !row.isVisible}
                    onChange={() => handleToggleRequired(row)}
                    red
                  />
                </td>

                {/* Active */}
                <td style={tdCenter}>
                  <Toggle
                    checked={row.isVisible}
                    disabled={row.isCore}
                    onChange={() => handleToggleVisible(row)}
                  />
                </td>

                {/* Actions */}
                <td style={tdCenter}>
                  {row.isCustom && (
                    <div style={{ display: 'flex', gap: 3, justifyContent: 'center' }}>
                      <button
                        type="button"
                        onClick={() => {
                          const f = allCustom.find((cf) => cf.id === row.id);
                          if (f) { setEditField(f); setAddOpen(true); }
                        }}
                        style={{
                          background: 'var(--bg-input)', border: '1px solid var(--border-input)',
                          borderRadius: 4, padding: '2px 5px', cursor: 'pointer',
                          fontSize: 11, color: 'var(--text-color)',
                        }}
                      >✏️</button>
                      <button
                        type="button"
                        onClick={() => deleteCustomField(row)}
                        style={{
                          background: 'var(--bg-input)', border: '1px solid var(--border-input)',
                          borderRadius: 4, padding: '2px 5px', cursor: 'pointer',
                          fontSize: 11, color: 'var(--text-color)',
                        }}
                      >🗑</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {visibleRows.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  style={{ ...tdStyle, textAlign: 'center', padding: 28, color: 'var(--text-color-calendar-week)' }}
                >
                  {search || filterSection ? 'No fields match the filter' : 'No fields'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <AddCustomField
        open={addOpen}
        onClose={() => { setAddOpen(false); setEditField(null); }}
        onSaved={() => load()}
        editField={editField}
        defaultSection={addSection}
      />
    </div>
  );
}
